import React, { useMemo } from "react";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";

interface PaginationWarehouseProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  range?: number;
  id?: string;
}

const PaginationWarehouse: React.FC<PaginationWarehouseProps> = ({
  page,
  totalPages,
  onPageChange,
  range = 1,
  id,
}) => {
  const safeTotalPages = Math.max(1, totalPages);
  const safePage = Math.min(Math.max(page, 1), safeTotalPages);

  const pages = useMemo(() => {
    const pages: number[] = [];

    const start = Math.max(1, safePage - range);
    const end = Math.min(safeTotalPages, safePage + range);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }, [safePage, safeTotalPages, range]);

  if (safeTotalPages <= 1) return null;

  return (
    <div
      id={id}
      className="flex items-center justify-between px-4 py-3 border border-gray-300 shadow-sm bg-white rounded-md"
    >
      <span className="text-sm text-muted-foreground hidden sm:block">
        Page <span className="font-semibold">{safePage}</span> of{" "}
        <span className="font-semibold">{safeTotalPages}</span>
      </span>

      <div className="flex items-center gap-1">
        <button
          id={id ? `${id}-prev` : undefined}
          onClick={() => onPageChange(safePage - 1)}
          disabled={safePage === 1}
          className="
            h-9 w-9 flex items-center justify-center rounded-md
            border border-border
            disabled:opacity-40 disabled:cursor-not-allowed
            hover:bg-muted
          "
        >
          <Icon path={mdiChevronLeft} size={0.8} />
        </button>

        {pages[0] > 1 && (
          <>
            <PageBtn active={safePage === 1} onClick={() => onPageChange(1)}>
              1
            </PageBtn>
            <Ellipsis />
          </>
        )}

        {pages.map((p) => (
          <PageBtn
            key={p}
            active={p === safePage}
            onClick={() => onPageChange(p)}
          >
            {p}
          </PageBtn>
        ))}

        {pages[pages.length - 1] < safeTotalPages && (
          <>
            <Ellipsis />
            <PageBtn
              active={safePage === safeTotalPages}
              onClick={() => onPageChange(safeTotalPages)}
            >
              {safeTotalPages}
            </PageBtn>
          </>
        )}

        <button
          id={id ? `${id}-next` : undefined}
          onClick={() => onPageChange(safePage + 1)}
          disabled={safePage === safeTotalPages}
          className="
            h-9 w-9 flex items-center justify-center rounded-md
            border border-border
            disabled:opacity-40 disabled:cursor-not-allowed
            hover:bg-muted
          "
        >
          <Icon path={mdiChevronRight} size={0.8} />
        </button>
      </div>
    </div>
  );
};

export default PaginationWarehouse;

const PageBtn: React.FC<{
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`
      min-w-[36px] h-9 px-2 rounded-md text-sm font-medium
      transition-colors
      ${active ? "bg-black text-white" : "text-gray-700 hover:bg-muted"}
    `}
  >
    {children}
  </button>
);

const Ellipsis = () => (
  <span className="px-2 text-muted-foreground select-none">…</span>
);
